import { type UseQueryResult, useQueries } from "@tanstack/react-query"
import type { Omit } from "@wagmi/core/internal"
import { useMemo } from "react"
import { Evaluate } from "viem"
import { useChainId, useConfig } from "wagmi"
import {
  type GetAccountInfoErrorType,
  getAccountInfo,
} from "../actions/getAccountInfo"
import {
  type GetAccountInfoData,
  type GetAccountInfoOptions,
  type GetAccountInfoQueryFnData,
  type GetAccountInfoQueryKey,
  getAccountInfoQueryKey,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
} from "../types"

export type UseAccountInfosProps<
  config extends ExtendedConfig = ExtendedConfig,
  selectedData = GetAccountInfoData,
  combineData = UseQueryResult<selectedData>[],
> = ConfigParameter<config> & {
  queries: Evaluate<
    GetAccountInfoOptions<config> &
      ConfigParameter<config> & {
        query?:
          | Evaluate<
              Omit<
                UseQueryParameters<
                  GetAccountInfoQueryFnData,
                  GetAccountInfoErrorType,
                  selectedData,
                  GetAccountInfoQueryKey<config>
                >,
                "gcTime" | "staleTime"
              >
            >
          | undefined
      }
  >[]
  select?: (data: GetAccountInfoData) => selectedData
  combine?: (result: UseQueryResult<selectedData>[]) => combineData
}

export function useAccountInfos<
  config extends ExtendedConfig,
  selectedData = GetAccountInfoData,
  combineData = UseQueryResult<selectedData>[],
>(
  parameters: UseAccountInfosProps<config, selectedData, combineData>,
): combineData {
  const { queries = [], select, combine } = parameters
  const config = useConfig(parameters)
  const chainId = useChainId({ config })

  const queryResults = useQueries({
    queries: queries.map(({ query = {}, ...options }) => ({
      ...query,
      queryKey: getAccountInfoQueryKey({
        ...options,
        chainId: options.chainId || chainId,
      }),
      queryFn: () =>
        getAccountInfo(config, {
          ...options,
          chainId: options.chainId || chainId,
        }),
      select: select,
      refetchOnMount: false,
      retryOnMount: false,
      retry: false,
      enabled: Boolean(
        options.vault && options.account && (query.enabled ?? true),
      ),
    })),
  }) satisfies UseQueryResult<selectedData>[]

  return useMemo(
    () =>
      combine
        ? combine(queryResults)
        : (queryResults as unknown as combineData),
    [queryResults, combine],
  )
}
